<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  toastId: { type: Number, required: true },
  request: { type: Object, required: true },
});

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const accountId = computed(() => route.params.accountId);

const contactName = computed(
  () => props.request.contact?.name || props.request.phone_number
);
const channelLabel = computed(
  () => props.request.conversation?.inbox_name || '—'
);

const dismiss = () => {
  store.dispatch('painelHumanAttention/dismissToast', props.toastId);
};

const openConversation = () => {
  store.dispatch('painelHumanAttention/startAttendance', props.request);
  dismiss();
  if (props.request.conversation?.display_id) {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: props.request.conversation.display_id },
    });
  } else {
    router.push(`/app/accounts/${accountId.value}/painel/atendimentos-humanos`);
  }
};
</script>

<template>
  <div
    class="relative w-[360px] rounded-xl border border-n-ruby-7 border-l-4 border-l-n-ruby-9 bg-n-solid-2 shadow-2xl p-4 flex gap-3 items-start animate-slide-in-right"
  >
    <div
      class="w-9 h-9 rounded-full bg-n-ruby-3 text-n-ruby-11 flex items-center justify-center text-base shrink-0 relative"
      aria-hidden="true"
    >
      <i class="i-ph-bell-ringing text-lg" />
      <span
        class="absolute inset-[-4px] rounded-full border-2 border-n-ruby-7 opacity-60 animate-ping"
      />
    </div>
    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2 mb-1.5">
        <span class="text-xs font-semibold text-n-slate-12">
          {{ t('PAINEL.HUMAN_ATTENTION.TOAST.TITLE') }}
        </span>
        <span
          class="text-[10px] font-semibold tracking-wide uppercase px-1.5 py-0.5 rounded bg-n-ruby-3 text-n-ruby-11"
        >
          {{ t('PAINEL.HUMAN_ATTENTION.TOAST.BADGE') }}
        </span>
      </div>
      <div class="text-sm font-medium text-n-slate-12 mb-0.5 truncate">
        {{ contactName }}
      </div>
      <div class="text-xs text-n-slate-11 mb-3 flex items-center gap-1.5">
        <span class="truncate">{{ request.phone_number }}</span>
        <span class="text-n-slate-9" aria-hidden="true">&middot;</span>
        <span class="truncate">{{ channelLabel }}</span>
      </div>
      <div class="flex gap-2">
        <button
          type="button"
          class="px-3 py-1.5 text-xs font-semibold rounded-md bg-n-ruby-9 text-white hover:bg-n-ruby-10 transition"
          @click="openConversation"
        >
          {{ t('PAINEL.HUMAN_ATTENTION.TOAST.OPEN_CONVERSATION') }}
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-xs font-medium rounded-md border border-n-strong text-n-slate-11 hover:bg-n-solid-3 transition"
          @click="dismiss"
        >
          {{ t('PAINEL.HUMAN_ATTENTION.TOAST.LATER') }}
        </button>
      </div>
    </div>
    <button
      type="button"
      :aria-label="t('PAINEL.HUMAN_ATTENTION.TOAST.CLOSE')"
      class="absolute top-2 right-2 text-n-slate-10 hover:text-n-slate-12 text-base leading-none px-1.5 py-0.5"
      @click="dismiss"
    >
      <i class="i-ph-x text-sm" aria-hidden="true" />
    </button>
  </div>
</template>
